"use client";

import { useState } from "react";
import { Button, useToast } from "@wmart/ui";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";

/**
 * One-click quick-add control for the catalog grid product tile.
 *
 * Idle (variant not in the cart): a single "+ Add" button. Clicking it adds
 * the MOQ immediately — no separate qty-build-then-Add step.
 *
 * In cart: a compact −/qty/+ stepper bound to the LIVE cart line:
 *  - + / − step by max(1, orderIncrement).
 *  - − below MOQ removes the line and returns to the idle "+ Add" button.
 *  - qty is READ from the cart line (not local state), so two tiles for the
 *    same variant stay in sync. Precise/bulk entry lives on the PDP
 *    (<QtyAndAdd>); the tile is quick-add only.
 */
interface TileAddToCartProps {
  variantId: string;
  productTitle: string;
  minOrderQuantity: number;
  orderIncrement: number;
  /** Starting variant stock gate. false → add/+ disabled ("Out of stock"). */
  inStock?: boolean;
}

export function TileAddToCart({
  variantId,
  productTitle,
  minOrderQuantity,
  orderIncrement,
  inStock = true,
}: TileAddToCartProps) {
  const moq = Math.max(1, minOrderQuantity);
  const inc = Math.max(1, orderIncrement);

  const [busy, setBusy] = useState(false);
  const { cart, addItem, updateQuantity, removeItem } = useCart();
  const toast = useToast();

  const line = cart?.lines.find((l) => l.variantId === variantId) ?? null;
  const qty = line?.quantity ?? 0;

  // Run a cart mutation behind a busy guard + danger toast. The guard also
  // closes the double-click race: qty is derived from the server cart, which
  // only updates once the await resolves, so a second click can't read a
  // stale line.quantity while the first is in flight.
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.push({
        tone: "danger",
        title: "Could not update cart",
        body: err instanceof Error ? err.message : "Try again",
      });
    } finally {
      setBusy(false);
    }
  };

  const onAdd = () =>
    run(async () => {
      await addItem({ variantId, quantity: moq });
      toast.push({
        tone: "success",
        title: "Added to cart",
        body: `${moq} × ${productTitle}`,
      });
    });

  const increment = () => {
    if (!line) return;
    void run(() => updateQuantity(line.id, line.quantity + inc));
  };

  const decrement = () => {
    if (!line) return;
    const next = line.quantity - inc;
    void run(() =>
      next < moq ? removeItem(line.id) : updateQuantity(line.id, next),
    );
  };

  // Out of stock + not yet in the cart: no add affordance at all.
  if (!line && !inStock) {
    return (
      <Button
        size="sm"
        variant="secondary"
        disabled
        className="w-full"
        aria-label={`${productTitle} is out of stock`}
      >
        Out of stock
      </Button>
    );
  }

  // Idle: single "+ Add" button — one click adds the MOQ immediately.
  if (!line) {
    return (
      <Button
        size="sm"
        variant="primary"
        icon="plus"
        onClick={onAdd}
        loading={busy}
        disabled={busy}
        className="w-full"
        aria-label={`Add ${productTitle} to cart`}
      >
        Add
      </Button>
    );
  }

  // In cart: −/qty/+ stepper bound to the live cart line.
  return (
    <div
      data-slot="tile-add-to-cart"
      className="flex h-8 w-full items-center rounded-sm border border-ink-200 bg-white"
    >
      <Button
        variant="ghost"
        size="sm"
        icon="minus"
        aria-label="Decrease quantity"
        onClick={decrement}
        disabled={busy}
      />
      <span
        data-slot="qty"
        aria-live="polite"
        aria-label={`Quantity in cart: ${qty}`}
        className="numeric w-full min-w-0 flex-1 select-none text-center text-sm text-ink-900"
      >
        {qty}
      </span>
      <Button
        variant="ghost"
        size="sm"
        icon="plus"
        aria-label="Increase quantity"
        onClick={increment}
        // Out of stock: block increases but still allow −/remove.
        disabled={busy || !inStock}
      />
    </div>
  );
}
